import { useState } from "react";
import authService from "../services/authService";
import type { User } from "../types/user";

type MfaState = {
  required: boolean;
  type: "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null;
  destination?: string;
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfa, setMfa] = useState<MfaState>({ required: false, type: null });

  /**
   * Step 1: Initiate login with Cognito.
   * If MFA is required, sets mfa.required = true so the UI can show the code input.
   */
  const login = async (username: string, password: string) => {
    console.log("[useAuth] login() called");
    setLoading(true);
    setError(null);
    try {
      console.log("[useAuth] calling authService.login()...");
      const result = await authService.login(username, password);
      console.log("[useAuth] authService.login() returned:", result);

      if (result.status === "mfa_required") {
        console.log("[useAuth] MFA required, showing code input");
        setMfa({ required: true, type: result.mfaType, destination: result.destination });
        return result;
      }

      // Direct success (no MFA)
      console.log("[useAuth] ✅ Login success! Token stored.");
      setUser(result.user);
      return result;
    } catch (err: any) {
      console.error("[useAuth] ❌ login() caught error:", err);
      const msg = typeof err === "string" ? err : err?.message || "An unexpected error occurred.";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Step 2: Confirm the MFA code the doctor entered.
   */
  const confirmMfa = async (code: string) => {
    if (!mfa.type) return;
    setLoading(true);
    setError(null);
    try {
      const result = await authService.confirmMfa(code, mfa.type);
      if (result.status === "success") {
        setMfa({ required: false, type: null });
        setUser(result.user);
      }
      return result;
    } catch (err: any) {
      const msg = typeof err === "string" ? err : err?.message || "Invalid code.";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout from both Cognito and local state.
   */
  const logout = () => {
    authService.logout();
    setUser(null);
    setMfa({ required: false, type: null });
  };

  // Keeping loginDoctor for backward compatibility — it now maps to login
  const loginDoctor = login;

  return { user, loading, error, mfa, login, loginDoctor, confirmMfa, logout };
}
